import React, { useMemo } from 'react';
import * as dagre from '@dagrejs/dagre';
import { colors, radius } from '../tokens';

// Edge-type → color mapping. Lets us filter and color-code.
const EDGE_COLORS = {
  family:   colors.timeline.call,    // purple-ish
  affiliation: colors.moves.solicit, // orange
  board:    colors.moves.cult,       // purple
  softCredit: colors.info,
  friend:   colors.textTertiary,
  household: colors.brand,
};

const NODE_W = 180;
const NODE_H = 56;
const AVATAR_R = 18;

const RelationshipMap = ({
  nodes = [],
  edges = [],
  activeEdgeTypes = null, // null = all
  selectedId,
  onSelect,
  width = 820,
  height = 480,
}) => {
  const filteredEdges = useMemo(() => (
    activeEdgeTypes == null
      ? edges
      : edges.filter(e => activeEdgeTypes.includes(e.category))
  ), [edges, activeEdgeTypes]);

  const visibleNodeIds = useMemo(() => {
    if (activeEdgeTypes == null) return new Set(nodes.map(n => n.id));
    const s = new Set();
    filteredEdges.forEach(e => { s.add(e.from); s.add(e.to); });
    return s;
  }, [nodes, filteredEdges, activeEdgeTypes]);

  const layout = useMemo(() => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({
      rankdir: 'LR',
      nodesep: 24,
      ranksep: 120,
      marginx: 40,
      marginy: 40,
    });
    g.setDefaultEdgeLabel(() => ({}));
    nodes.forEach(n => {
      if (!visibleNodeIds.has(n.id)) return;
      g.setNode(n.id, { width: NODE_W, height: NODE_H });
    });
    filteredEdges.forEach((e, i) => {
      if (!visibleNodeIds.has(e.from) || !visibleNodeIds.has(e.to)) return;
      g.setEdge(e.from, e.to, { id: e.id || `edge-${i}` });
    });
    dagre.layout(g);
    return g;
  }, [nodes, filteredEdges, visibleNodeIds]);

  const graphWidth = layout.graph().width || width;
  const graphHeight = layout.graph().height || height;

  return (
    <svg
      viewBox={`0 0 ${graphWidth} ${graphHeight}`}
      width="100%"
      height={Math.min(graphHeight, 560)}
      style={{ background: colors.surfaceInk, borderRadius: radius.md }}
      role="img"
      aria-label="Relationship graph"
    >
      {/* Edges */}
      {filteredEdges.map((e, i) => {
        if (!visibleNodeIds.has(e.from) || !visibleNodeIds.has(e.to)) return null;
        const edgeKey = `${e.from}-${e.to}-${i}`;
        let graphEdge;
        try { graphEdge = layout.edge(e.from, e.to); } catch { return null; }
        if (!graphEdge || !graphEdge.points || graphEdge.points.length < 2) return null;
        const pts = graphEdge.points;
        const d = pts.map((p, idx) =>
          idx === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`
        ).join(' ');
        const mid = pts[Math.floor(pts.length / 2)];
        const color = EDGE_COLORS[e.category] || colors.textTertiary;
        return (
          <g key={edgeKey}>
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeDasharray={e.former ? '4 4' : undefined}
              opacity={0.6}
            />
            {e.label && (
              <g transform={`translate(${mid.x},${mid.y})`}>
                <rect
                  x={-e.label.length * 3.2 - 6}
                  y={-8}
                  width={e.label.length * 6.4 + 12}
                  height={16}
                  rx={8}
                  fill={colors.surface}
                  stroke={color}
                />
                <text
                  textAnchor="middle"
                  dominantBaseline="central"
                  style={{ fontSize: 10, fontWeight: 700, fill: color }}
                >
                  {e.label}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map(n => {
        if (!visibleNodeIds.has(n.id)) return null;
        const pos = layout.node(n.id);
        if (!pos) return null;
        const x = pos.x - NODE_W / 2;
        const y = pos.y - NODE_H / 2;
        const isSelected = selectedId === n.id;
        const isRoot = n.isRoot;

        return (
          <g
            key={n.id}
            transform={`translate(${x},${y})`}
            onClick={() => onSelect && onSelect(n.id)}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}
          >
            <rect
              x={0}
              y={0}
              width={NODE_W}
              height={NODE_H}
              rx={8}
              fill={colors.surface}
              stroke={isSelected ? colors.brand : (isRoot ? colors.brand : colors.border)}
              strokeWidth={isSelected || isRoot ? 2 : 1}
            />
            <circle
              cx={AVATAR_R + 8}
              cy={NODE_H / 2}
              r={AVATAR_R}
              fill={n.avatarColor || colors.brand}
            />
            <text
              x={AVATAR_R + 8}
              y={NODE_H / 2}
              textAnchor="middle"
              dominantBaseline="central"
              style={{ fontSize: 12, fontWeight: 700, fill: '#fff' }}
            >
              {n.initials}
            </text>
            <text
              x={AVATAR_R * 2 + 18}
              y={NODE_H / 2 - 6}
              style={{
                fontSize: 13,
                fontWeight: 700,
                fill: colors.textPrimary,
              }}
            >
              {truncate(n.label, 18)}
            </text>
            <text
              x={AVATAR_R * 2 + 18}
              y={NODE_H / 2 + 10}
              style={{
                fontSize: 11,
                fontWeight: 500,
                fill: colors.textTertiary,
              }}
            >
              {truncate(n.subtitle || '', 22)}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const truncate = (s, max) => (s && s.length > max ? `${s.slice(0, max - 1)}…` : s || '');

export default RelationshipMap;
